import React from "react";
import { Pagination as PaginationUI, SpaceCard, SpaceList } from "@caelor/ui";
import {
  SpaceCardContainer,
  SpaceListContainer,
  PaginationWrapper,
  Link,
} from "./styled";
import {
  NewPaginationTypesMapper,
  PaginationTypes,
} from "@Shared/Pagination-Types";
import { useTranslation } from "react-i18next";
import { useSpacesStore } from "../../../stores/use-space-store";
import { useUserNamesFromSpaces } from "../useSpaces";
import { SpaceElements } from "../../../optics/components/drawers/settings/features/spaces/defaults";

const LinkWrapper = ({ url, mode, openInNewTab, children }) => {
  if (mode === "view") {
    return (
      <Link
        href={url}
        target={openInNewTab ? "_blank" : "_top"}
        rel="noreferrer"
      >
        {children}
      </Link>
    );
  }
  return children;
};

const Mapper = ({ data, children }) => {
  return data.map((space) => children(space));
};

const Variant = (props) => {
  const { variant, children } = props;

  if (variant === "card") {
    return (
      <SpaceCardContainer {...props}>{children(SpaceCard)}</SpaceCardContainer>
    );
  }
  if (variant === "list") {
    return (
      <SpaceListContainer {...props}>{children(SpaceList)}</SpaceListContainer>
    );
  }
};

const Pagination = ({ data, pagination, queryPagination, children }) => {
  const { t } = useTranslation();

  const buildData = () => {
    if (pagination === PaginationTypes.INFINITE || queryPagination.disabled) {
      return Object.keys(data || {})?.flatMap((key) => data[key]) || [];
    }
    if (pagination === PaginationTypes.CAROUSEL) {
      return data[page] || [];
    }
  };

  if (queryPagination.disabled) return children(buildData());

  const { hasNextPage, hasPrevPage, onNext, onPrev, page } = queryPagination;

  return (
    <PaginationWrapper>
      {children(buildData())}
      <PaginationUI
        type={NewPaginationTypesMapper[pagination]}
        labels={{
          next:
            pagination === PaginationTypes.INFINITE
              ? t("show-more")
              : t("next"),
          prev: t("prev"),
        }}
        hasNext={hasNextPage}
        hasPrev={hasPrevPage}
        onNext={() => onNext()}
        onPrev={() => onPrev()}
      />
    </PaginationWrapper>
  );
};

function SpacesPreview({ data, queryPagination, mode = "edit" }) {
  const { config } = useSpacesStore((state) => ({
    config: state.data.config,
  }));

  const {
    variant,
    columns,
    pagination,
    activeElements,
    cardBorderRadius,
    openInNewTab,
  } = config;

  const shouldShowElement = (element) => activeElements?.includes(element);
  const userNames = useUserNamesFromSpaces(data);

  return (
    <Pagination
      data={data}
      pagination={pagination}
      queryPagination={queryPagination}
    >
      {(paginatedData) => (
        <Variant variant={variant} columns={columns}>
          {(SpaceComponent) => (
            <Mapper data={paginatedData}>
              {(space) => {
                const displayName = userNames[space.creatorId] || "Loading...";

                return (
                  <LinkWrapper
                    url={space.url}
                    mode={mode}
                    openInNewTab={openInNewTab ?? true}
                  >
                    <SpaceComponent
                      {...space}
                      key={space.spaceKey}
                      name={space?.name}
                      description={
                        shouldShowElement(SpaceElements.DESCRIPTION)
                          ? space.description
                          : null
                      }
                      authorName={
                        shouldShowElement(SpaceElements.AUTHOR)
                          ? displayName
                          : null
                      }
                      authorUrl={space.authorUrl}
                      spaceIcon={`${window.AP._hostOrigin}${space.icon.path}`}
                      borderRadius={cardBorderRadius}
                    />
                  </LinkWrapper>
                );
              }}
            </Mapper>
          )}
        </Variant>
      )}
    </Pagination>
  );
}

export default SpacesPreview;
